import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Types
import { IUpdateRepairOrdersSubscriptionResponse } from "../types/model";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";

// Hooks
import { getRepairOrdersQueryKey } from "./useRepairOrdersQuery";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Helpers
import { or } from "@/Common/helpers/sonar";

// Subscriptions
import { onUpdateRepairOrders } from "@/DataPages/api/repairOrders/subscriptions";

export function useRepairOrdersUpdateSubscription(args: { locationId: string; commissionCycleId: string; payPeriodId: string }) {
	const { locationId, commissionCycleId, payPeriodId } = args;

	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const subscriptionKey = ["repairOrdersSub", locationId, commissionCycleId, payPeriodId];

	useEffect(() => {
		if (or(!locationId, !commissionCycleId, !payPeriodId)) {
			return;
		}

		addSubscription(subscriptionKey, [
			{
				context: "onUpdateRepairOrders",
				query: onUpdateRepairOrders,
				variables: {
					commissionCycleId,
					payPeriodId,
				},
			},
		]);

		return () => {
			deleteSubscription(subscriptionKey);
		};
	}, [locationId, commissionCycleId, payPeriodId]);

	const repairOrdersSubscription = getSubscription(subscriptionKey);

	repairOrdersSubscription?.subscriptions?.onUpdateRepairOrders?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateRepairOrders", IUpdateRepairOrdersSubscriptionResponse>) => {
			const hasArrayResponse = Array.isArray(result.data?.onUpdateRepairOrders?.repairOrders);
			if (!hasArrayResponse) {
				return;
			}
			const queryKey = getRepairOrdersQueryKey({ locationId, commissionCycleId, payPeriodId });
			queryClient.invalidateQueries({
				queryKey,
			});
		}
	);
}
